import PropTypes from "prop-types";
import { createContext, useCallback, useContext, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ApplyTagDialogContent from "components/tags/ApplyTagDialogContent";

const ApplyTagDialogContext = createContext(null);

/**
 * Owns the one Apply Tag dialog, so a table of tag pills mounts a single
 * dialog. A click outside does not dismiss it, so entered values are not lost
 * by accident. `request` outlives `isOpen` so the tag stays rendered while the
 * exit animation runs.
 */
const ApplyTagDialogProvider = ({ children }) => {
  const [request, setRequest] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const openApplyTagDialog = useCallback((tag, onSubmit, modelType) => {
    setRequest({ tag, onSubmit, modelType });
    setIsOpen(true);
  }, []);

  return (
    <ApplyTagDialogContext.Provider value={openApplyTagDialog}>
      {children}
      <Dialog open={isOpen} onOpenChange={setIsOpen} disablePointerDismissal>
        <DialogContent className="tw:sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Apply Tag</DialogTitle>
          </DialogHeader>
          {request && (
            <ApplyTagDialogContent
              key={request.tag.id}
              tag={request.tag}
              modelType={request.modelType}
              onSubmit={request.onSubmit}
              onSuccess={() => setIsOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </ApplyTagDialogContext.Provider>
  );
};

ApplyTagDialogProvider.propTypes = {
  children: PropTypes.node,
};

const useApplyTagDialog = () => {
  const openApplyTagDialog = useContext(ApplyTagDialogContext);
  if (!openApplyTagDialog) {
    throw new Error(
      "useApplyTagDialog must be used inside an ApplyTagDialogProvider"
    );
  }
  return openApplyTagDialog;
};

export { ApplyTagDialogProvider, useApplyTagDialog };
